import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import StepListSetupModal from '../StepListSetup/StepListSetupModal';
import API_BASE_URL from '../../../../../Constants';

// =================== CONFIG ARRAY ===================
//
// Instead of importing from "./InspectionDeviceEditConfig",
// let's define inline for simplicity. If you prefer, 
// keep it in a separate file. Just make sure that you have 
// 'scheduledForDate' and 'scheduledForTime' fields included.
//
const inspectionDeviceEditFields = [
  {
    key: 'status',
    label: 'Current Step',
    type: 'select', // We'll fill <select> with stepOptions
  },
  {
    key: 'scheduledForDate',
    label: 'Scheduled For Date',
    type: 'date',
  },
  {
    key: 'scheduledForTime',
    label: 'Scheduled For Time',
    type: 'time', // using HTML5 input type="time"
  },
  {
    key: 'servicer',
    label: 'Assigned To',
    type: 'select',
  },
  {
    key: 'coverage',
    label: 'Coverage',
    type: 'text',
  },
  {
    key: 'performTest',
    label: 'Perform Test?',
    type: 'checkbox',
  },
  {
    key: 'witnessTest',
    label: 'Witness Test?',
    type: 'checkbox',
  },
];

// =================== HELPER FUNCTIONS ===================
function normalize(str) {
  return str === '' ? null : str;
}

function fallbackOr(parentValue, deviceValue) {
  if (deviceValue !== null && deviceValue !== undefined && deviceValue !== '') {
    return deviceValue;
  }
  return parentValue ?? '';
}

/**
 * Combine separate date/time into an ISO-like string, e.g. "2025-04-10T13:30:00".
 */
function buildScheduleDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const safeTime = timeStr && timeStr.trim() !== '' ? timeStr.trim() : '00:00';
  return `${dateStr}T${safeTime}:00`;
}

/**
 * If server returns "YYYY-MM-DDTHH:MM:SS", split into separate date/time.
 */
function parseScheduleDateTime(isoString) {
  if (!isoString) return { date: null, time: null };

  const [datePart, timePart] = isoString.split('T');
  let date = datePart || null;
  let time = null;
  if (timePart) {
    // For example, "13:30:00" => just "13:30"
    time = timePart.slice(0, 5);
  }
  return { date, time };
}

// Service-company dropdown fields (optional)
const deviceDropdownFields = [
  {
    key: 'maintenanceCoId',
    label: 'Maintenance Co (Device-Specific)',
    type: 'serviceCompanyDropdown',
  },
  {
    key: 'performingCoId',
    label: 'Performing Co (Device-Specific)',
    type: 'serviceCompanyDropdown',
  },
  {
    key: 'witnessCoId',
    label: 'Witness Co (Device-Specific)',
    type: 'serviceCompanyDropdown',
  },
];

// =================== MAIN COMPONENT ===================
export default function InspectionDeviceEdit({
  open,
  onClose,
  // Possibly multiple devices in a list
  devices = [],
  deviceIndex = 0,

  // Basic props
  currentStep,
  inspectionNumber,
  deviceId,
  categoryInspection,
  buildingSite,
  agencyId,
  inspYear,
  inspectionStatus,
  scheduledDate, // might not be used
  inspectionDate,
  initialData = {},
  onSave,
  onOpenViolationsModal,
  violationNumber = '',
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [deviceData, setDeviceData] = useState({});
  const [openStepListSetup, setOpenStepListSetup] = useState(false);
  const [stepOptions, setStepOptions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(deviceIndex);
  const [inspectionRecord, setInspectionRecord] = useState(null);
  const [allServiceCompanies, setAllServiceCompanies] = useState([]);
  const [allInspectors, setAllInspectors] = useState([]);

  const dialogHeight = isExpanded ? '90%' : '64%';

  // 1) Fetch master inspection
  useEffect(() => {
    if (!open) return;
    if (!inspectionNumber) return;

    async function fetchMasterInspection() {
      try {
        const url = `${API_BASE_URL}/api/inspections/id/${inspectionNumber}`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          const insp = await res.json();
          setInspectionRecord(insp);
        } else {
          console.warn('No master inspection =>', res.status);
          setInspectionRecord(null);
        }
      } catch (err) {
        console.error('Failed to fetch master inspection =>', err);
        setInspectionRecord(null);
      }
    }

    fetchMasterInspection();
  }, [open, inspectionNumber]);

  // 1b) fetch service companies
  useEffect(() => {
    if (!open) return;

    const fetchServiceCompanies = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/inspections/service-companies`,
          { credentials: 'include' }
        );
        if (!res.ok) {
          console.warn('Could not fetch service companies =>', res.status);
          return;
        }
        const data = await res.json();
        if (Array.isArray(data)) {
          setAllServiceCompanies(data);
        }
      } catch (error) {
        console.error('Error fetching service companies =>', error);
      }
    };
    fetchServiceCompanies();
  }, [open]);

  // 2) load bridging row
  useEffect(() => {
    if (!open) return;

    if (devices.length > 0) {
      if (currentIndex < 0 || currentIndex >= devices.length) return;
      const dev = devices[currentIndex];
      if (dev?.deviceId) {
        loadInspectionDeviceRow(
          dev.inspectionId || inspectionNumber,
          dev.deviceId
        );
      }
    } else if (inspectionNumber && deviceId) {
      loadInspectionDeviceRow(inspectionNumber, deviceId);
    }
  }, [open, currentIndex, devices, inspectionNumber, deviceId]);

  useEffect(() => {
    if (open) setCurrentIndex(deviceIndex);
  }, [open, deviceIndex]);

  // actual fetch for bridging row
  async function loadInspectionDeviceRow(inspId, devId) {
    try {
      const url = `${API_BASE_URL}/api/inspection-devices/${inspId}/${devId}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        console.warn('No bridging row =>', response.status);
        mergeParentProps(null);
        return;
      }
      const row = await response.json();
      mergeParentProps(row);
    } catch (err) {
      console.error('Failed to load bridging row:', err);
      mergeParentProps(null);
    }
  }

  // 2b) (optional) fetch violation count
  useEffect(() => {
    if (!open) return;
    if (!inspectionNumber || !deviceId) return;

    (async () => {
      try {
        const url = `${API_BASE_URL}/api/violations/count/inspection/${inspectionNumber}/device/${deviceId}`;
        const res = await fetch(url);
        if (res.ok) {
          const count = await res.json();
          setDeviceData((prev) => ({ ...prev, violationCount: count }));
        } else {
          console.warn('No violation count =>', res.status);
        }
      } catch (err) {
        console.error('Failed to fetch violation count =>', err);
      }
    })();
  }, [open, inspectionNumber, deviceId]);

  // 3) merges bridging row + parent's fallback
  function mergeParentProps(row) {
    const devMaint = normalize(row?.maintenanceCoId);
    const devPerf = normalize(row?.performingCoId);
    const devWit = normalize(row?.witnessCoId);

    const parMaint = normalize(initialData.maintenanceCoId);
    const parPerf = normalize(initialData.performingCoId);
    const parWit = normalize(initialData.witnessCoId);

    const inspMaint = normalize(inspectionRecord?.maintenanceCoId);
    const inspPerf = normalize(inspectionRecord?.performingCoId);
    const inspWit = normalize(inspectionRecord?.witnessCoId);

    const finalMaint = fallbackOr(fallbackOr(inspMaint, parMaint), devMaint);
    const finalPerf = fallbackOr(fallbackOr(inspPerf, parPerf), devPerf);
    const finalWit = fallbackOr(fallbackOr(inspWit, parWit), devWit);

    setDeviceData((prev) => ({
      ...prev,
      inspectionNumber: row?.id?.inspectionId || inspectionNumber || '',
      deviceId: row?.id?.deviceId || deviceId || '',
      status: normalize(row?.status) ?? prev.status ?? '',
      coverage: normalize(row?.coverage) ?? prev.coverage ?? '',
      performTest: row?.performTest ?? prev.performTest ?? false,
      witnessTest: row?.witnessTest ?? prev.witnessTest ?? false,
      violationCount: row?.violationCount ?? prev.violationCount ?? 0,
      violationNumber: row?.violationNumber || prev.violationNumber || violationNumber || '',
      servicer: row?.servicer || prev.servicer || null,

      maintenanceCoId: finalMaint,
      performingCoId: finalPerf,
      witnessCoId: finalWit,

      // scheduledForDate/time from DB if present
      scheduledForDate: row?.scheduledForDate ?? prev.scheduledForDate ?? null,
      scheduledForTime: row?.scheduledForTime ?? prev.scheduledForTime ?? null,
    }));
  }

  // 4) Merge top-level props
  useEffect(() => {
    if (!open) return;
    setDeviceData((prev) => ({
      ...prev,
      categoryInspection: prev.categoryInspection || categoryInspection || '',
      buildingSite: prev.buildingSite || buildingSite || '',
      agencyId: prev.agencyId || agencyId || '',
      scheduledDate: prev.scheduledDate || scheduledDate || '',
      inspectionDate: prev.inspectionDate || inspectionDate || '',
      inspYear: prev.inspYear || inspYear || '',
      inspectionStatus: prev.inspectionStatus || inspectionStatus || '',
    }));
  }, [
    open,
    categoryInspection,
    buildingSite,
    agencyId,
    scheduledDate,
    inspectionDate,
    inspYear,
    inspectionStatus,
  ]);

  // 5) fetch stepList if needed
  useEffect(() => {
    if (!open) return;
    if (!deviceData.agencyId || !deviceData.categoryInspection) return;
    if (deviceData.stepListId) return;

    const fetchStepList = async () => {
      try {
        const url = `${API_BASE_URL}/api/step-lists/search?agencyId=${deviceData.agencyId}&testId=${encodeURIComponent(deviceData.categoryInspection)}`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          const found = await res.json();
          if (found?.stepListId) {
            setDeviceData((p) => ({ ...p, stepListId: found.stepListId }));
          }
        }
      } catch {/* ignore */}
    };
    fetchStepList();
  }, [open, deviceData.agencyId, deviceData.categoryInspection, deviceData.stepListId]);

  // 6) fetch step items if we have stepListId
  useEffect(() => {
    if (!open) return;
    if (!deviceData.stepListId) return;

    async function loadSteps() {
      try {
        const url = `${API_BASE_URL}/api/step-lists/${deviceData.stepListId}`;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) {
          console.warn('Step list fetch failed =>', resp.status);
          return;
        }
        const data = await resp.json();
        if (Array.isArray(data.items)) {
          const steps = data.items.map((item) => item.stepName);
          setStepOptions(steps);
        }
      } catch (err) {
        console.error('Failed to fetch step items =>', err);
      }
    }
    loadSteps();
  }, [open, deviceData.stepListId]);

  // if we have a currentStep => fill status if missing
  useEffect(() => {
    if (open && currentStep) {
      setDeviceData((prev) => {
        if (!prev.status) {
          return { ...prev, status: currentStep };
        }
        return prev;
      });
    }
  }, [open, currentStep]);

  // Load inspectors for servicer
  useEffect(() => {
    const fetchInspectors = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/inspectors`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch inspectors. Status: ${res.status}`);
        }
        const data = await res.json();
        setAllInspectors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching inspectors:", err);
      }
    };
    fetchInspectors();
  }, []);

  // handleSave => PUT
  const handleSave = async () => {
    try {
      if (!deviceData.inspectionNumber || !deviceData.deviceId) {
        alert('Missing composite key => cannot update row.');
        return;
      }
      // Build localDateTime from separate date/time
      const scheduleDateTime = buildScheduleDateTime(
        deviceData.scheduledForDate,
        deviceData.scheduledForTime
      );

      const url = `${API_BASE_URL}/api/inspection-devices/${deviceData.inspectionNumber}/${deviceData.deviceId}`;
      const body = {
        status: deviceData.status || null,
        coverage: deviceData.coverage || null,
        performTest: deviceData.performTest || false,
        witnessTest: deviceData.witnessTest || false,
        maintenanceCoId: deviceData.maintenanceCoId || null,
        performingCoId: deviceData.performingCoId || null,
        witnessCoId: deviceData.witnessCoId || null,
        servicer: deviceData.servicer || null,
        // Final field that the server can store in schedule_date_time
        scheduleDateTime: scheduleDateTime,
      };

      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        console.error('PUT failed =>', response.status);
        alert(`Update failed with status ${response.status}`);
      } else {
        const updated = await response.json();
        console.log('Updated =>', updated);
        alert(`Device-level fields updated! (status => ${updated.status})`);
      }
      if (onSave) onSave(deviceData);
    } catch (err) {
      console.error('Failed to update =>', err);
      alert('Error updating device row');
    }
  };

  // expand/collapse
  const handleToggleExpand = () => setIsExpanded(!isExpanded);
  const handleModalClose = () => onClose();

  // handle field changes
  const handleFieldChange = (key, value) => {
    setDeviceData((p) => ({ ...p, [key]: value }));
  };

  // stepList setup
  const handleOpenStepListSetup = () => setOpenStepListSetup(true);
  const handleCloseStepListSetup = (payload) => {
    setOpenStepListSetup(false);
    if (payload?.stepListId) {
      setDeviceData((prev) => ({ ...prev, stepListId: payload.stepListId }));
    }
  };

  // optional link if violations exist
  const renderViolationCount = () => {
    if (!deviceData.violationCount || deviceData.violationCount === 0) return null;
    return (
      <div className="text-sm text-red-600 font-semibold">
        <button
          type="button"
          onClick={() => {
            if (onOpenViolationsModal && deviceData.deviceId) {
              onOpenViolationsModal(deviceData.deviceId);
            }
          }}
          className="underline text-red-600"
        >
          Violations: {deviceData.violationCount}
        </button>
      </div>
    );
  };

  // For multiple devices
  const handlePrev = () => setCurrentIndex((p) => Math.max(p - 1, 0));
  const handleNext = () => setCurrentIndex((p) => Math.min(p + 1, devices.length - 1));

  // Generic field renderer
  function renderField(field) {
    const { key, label, type } = field;
    const value = deviceData[key] ?? '';

    // If the field is "status" with type=select => show stepOptions
    if (key === 'status' && type === 'select') {
      return (
        <select
          className="border border-gray-300 rounded p-1 w-full"
          value={value || ''}
          onChange={(e) => handleFieldChange(key, e.target.value)}
        >
          <option value="">-- Select Step --</option>
          {stepOptions.map((step) => (
            <option key={step} value={step}>
              {step}
            </option>
          ))}
        </select>
      );
    }

    if (key === 'servicer' && type === 'select') {
      return (
        <select
          className="border border-gray-300 rounded p-1 w-full"
          value={value || ''}
          onChange={(e) => handleFieldChange(key, e.target.value)}
        >
          <option value="">-- Select Inspector --</option>
          {allInspectors.map((insp) => (
            <option key={insp.id} value={insp.id}>
              {insp.inspectorName
                      ? `${insp.inspectorName} (#${insp.id})`
                      : `Inspector #${insp.id}`}
            </option>
          ))}
        </select>
      );
    }

    // If it's a serviceCompanyDropdown:
    if (type === 'serviceCompanyDropdown') {
      return (
        <select
          className="border border-gray-300 rounded p-1 w-full"
          value={value || ''}
          onChange={(e) => handleFieldChange(key, e.target.value)}
        >
          <option value="">-- Select --</option>
          {allServiceCompanies.map((co) => (
            <option key={co.serviceCompanyId} value={co.serviceCompanyId}>
              {co.companyName}
            </option>
          ))}
        </select>
      );
    }

    // other types
    switch (type) {
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => handleFieldChange(key, e.target.checked)}
          />
        );
      case 'textarea':
        return (
          <textarea
            className="border border-gray-300 rounded p-1 w-full"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        );
      case 'date':
      case 'time':
      case 'number':
      case 'text':
      default:
        return (
          <input
            type={type}
            className="border border-gray-300 rounded p-1 w-full"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        );
    }
  }

  // Render device-specific dropdowns in a box
  function renderDeviceDropdownsBox() {
    return (
      <div className="border border-gray-300 p-4 rounded-md bg-white mt-6">
        <h3 className="text-sm font-semibold mb-3">
          Device-Specific Service Companies
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {deviceDropdownFields.map((field) => (
            <div key={field.key} className="flex flex-col">
              <label className="font-semibold text-xs mb-1">{field.label}</label>
              {renderField(field)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        style: {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isExpanded ? '900px' : '700px',
          height: dialogHeight,
          zIndex: 3000,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      style={{ zIndex: 3000 }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-300">
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleModalClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-xl font-bold">Inspection Device Edit</h2>
          </div>
          <button
            onClick={handleToggleExpand}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>
      </div>

      <DialogContent dividers style={{ flex: '1 1 auto', overflow: 'auto' }}>
        {/* White card => top area */}
        <div className="bg-white border border-gray-300 p-4 rounded-md mb-6">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Inspection #
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.inspectionNumber || 'N/A'}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Device ID
              </label>
              <div className="font-semibold text-gray-900 flex items-center space-x-2">
                {devices.length > 1 && (
                  <button
                    onClick={handlePrev}
                    disabled={currentIndex <= 0}
                    className="px-2 py-1 bg-white border rounded hover:bg-gray-100"
                  >
                    &larr;
                  </button>
                )}
                <span>{deviceData.deviceId || 'N/A'}</span>
                {devices.length > 1 && (
                  <button
                    onClick={handleNext}
                    disabled={currentIndex >= devices.length - 1}
                    className="px-2 py-1 bg-white border rounded hover:bg-gray-100"
                  >
                    &rarr;
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Violation #
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.violationNumber || 'N/A'}
                {deviceData.violationNumber && (
                  <>
                    {' '}
                    <button
                      onClick={() =>
                        onOpenViolationsModal && deviceData.deviceId
                          ? onOpenViolationsModal(deviceData.deviceId)
                          : null
                      }
                      className="underline text-blue-600"
                    >
                      View Conditions
                    </button>
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Building Site
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.buildingSite || 'N/A'}
              </div>
            </div>
          </div>

          {renderViolationCount()}

          {/* Additional details row */}
          <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Category Insp
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.categoryInspection || 'N/A'}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Agency ID
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.agencyId || 'N/A'}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Insp Status
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.inspectionStatus || 'N/A'}
              </div>
            </div>
          </div>

          {/* Another row: year, etc */}
          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Inspection Year
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.inspYear || 'N/A'}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Inspection Date
              </label>
              <div className="font-semibold text-gray-900">
                {deviceData.inspectionDate || 'N/A'}
              </div>
            </div>
          </div>

          {/* Render the main form fields (including scheduledForDate/time) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {inspectionDeviceEditFields
              // If you want to exclude certain fields, do so here:
              // .filter(f => f.key !== 'blah')
              .map((field) => (
                <div key={field.key} className="flex flex-col">
                  <label className="font-semibold text-xs mb-1">
                    {field.label}
                  </label>
                  {renderField(field)}
                </div>
              ))}
          </div>
        </div>

        {/* Device-Specific Service Companies box */}
        {renderDeviceDropdownsBox()}

        {/* Master fallback info */}
        <div className="bg-gray-100 border border-gray-300 p-4 rounded-md mt-6">
          <h3 className="text-sm font-semibold mb-3">Service Companies</h3>
          <p className="text-xs text-gray-500 mb-2">
            These are the MASTER inspection service-company IDs. If blank above,
            these apply.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Master Maintenance
              </label>
              <div className="font-semibold text-gray-900">
                {inspectionRecord?.maintenanceCoId || 'N/A'}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Master Performing
              </label>
              <div className="font-semibold text-gray-900">
                {inspectionRecord?.performingCoId || 'N/A'}
              </div>
            </div>
            <div>
              <label className="font-semibold block text-xs mb-1 text-gray-700">
                Master Witness
              </label>
              <div className="font-semibold text-gray-900">
                {inspectionRecord?.witnessCoId || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Footer */}
      <div className="flex justify-end space-x-3 p-4 border-t border-gray-300">
        <button
          onClick={handleModalClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded hover:bg-gray-400 transition"
        >
          Close
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition"
        >
          Save
        </button>
      </div>

      {openStepListSetup && (
        <StepListSetupModal
          open={openStepListSetup}
          onClose={handleCloseStepListSetup}
          currentStepListId={deviceData.stepListId || null}
          currentAgencyId={deviceData.agencyId || ''}
          currentTestType={deviceData.categoryInspection || ''}
        />
      )}
    </Dialog>
  );
}
